// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The Posegraf tool's inspector (#407, #464, frontend-r22.7): the selected
 * edge (type, endpoints with thumbnails, residual, weight), its actions —
 * Sammenlign billederne, Kør ICP on the pair, Afvis kant (armed) — the
 * Tilføj kant form (ICP check and a suggested weight), the edges rejected in
 * this session with Genopret, and Kør optimering with the job's progress.
 *
 * "Afvis" is the contract's DELETE: ruxd marks the edge rejected rather than
 * erasing it, and a POST with the same pair and type brings it back — which
 * is what Genopret sends. Every write control is behind `useCanEdit()`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api/client';
import type { IcpRefineResult, PoseGraph, PoseGraphEdge, PoseGraphEdgeType } from '../api/types';
import { useCanEdit } from '../app/CaseRoleContext';
import { useJobs } from '../app/JobsContext';
import { useArmedConfirm } from '../app/useArmedConfirm';
import {
  EDGE_TYPES,
  EDGE_TYPE_LABEL,
  edgeParam,
  edgeSwatchCss,
  emptyDraft,
  formatResidual,
  formatWeight,
  icpVerdict,
  latestOptimizeJob,
  optimizeStatusText,
  poseGraphErrorCopy,
  restoreBody,
  suggestWeight,
  validateEdgeDraft,
  type EdgeDraft,
  type EdgeRef,
} from '../posegraf/model';
import { Button } from './Button';
import { IconButton, XIcon } from './IconButton';
import { Select } from './Select';
import { TextInput } from './TextInput';
import { TextLink } from './TextLink';
import controls from './controls.module.css';
import styles from './PoseGraphEdgeInspector.module.css';

export interface AddSeed {
  from: number | null;
  to: number | null;
  /** Bumped per request, so asking twice with the same pair reopens the form. */
  nonce: number;
}

export interface PoseGraphEdgeInspectorProps {
  graph: PoseGraph | null;
  /** The selected edge as stored, or null. */
  edge: PoseGraphEdge | null;
  /** Called after any successful write so the page reloads the graph. */
  onChanged: () => void;
  /** Select an edge (after an add) or clear the selection. */
  onSelect: (ref: EdgeRef | null) => void;
  /** Open the image compare on a pair. */
  onCompare: (from: number, to: number) => void;
  /** A request to open Tilføj kant with a pair filled in (from the image compare). */
  addSeed: AddSeed | null;
}

export function PoseGraphEdgeInspector({ graph, edge, onChanged, onSelect, onCompare, addSeed }: PoseGraphEdgeInspectorProps) {
  const canEdit = useCanEdit();
  const [rejected, setRejected] = useState<PoseGraphEdge[]>([]);
  const [edits, setEdits] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<EdgeDraft>(emptyDraft());
  const selKey = edge ? edgeParam(edge) : null;
  const confirm = useArmedConfirm<string>(busy, selKey);

  // A pair handed over from the image compare opens the form on it.
  useEffect(() => {
    if (!addSeed) return;
    setDraft(emptyDraft(addSeed.from, addSeed.to));
    setAddOpen(true);
  }, [addSeed]);

  useEffect(() => setError(null), [selKey]);

  const nodes = useMemo(() => (graph ? new Set(graph.nodes.map((n) => n.id)) : null), [graph]);

  const reject = useCallback(async () => {
    if (!edge) return;
    setBusy(true);
    setError(null);
    try {
      await api.deletePoseGraphEdge(edge.from, edge.to, edge.type);
      setRejected((list) => [edge, ...list.filter((e) => edgeParam(e) !== edgeParam(edge))]);
      setEdits((n) => n + 1);
      onSelect(null);
      onChanged();
    } catch (err) {
      setError(poseGraphErrorCopy(err, 'reject'));
    } finally {
      setBusy(false);
    }
  }, [edge, onChanged, onSelect]);

  const restore = useCallback(
    async (e: PoseGraphEdge) => {
      setBusy(true);
      setError(null);
      try {
        await api.addPoseGraphEdge(restoreBody(e));
        setRejected((list) => list.filter((x) => edgeParam(x) !== edgeParam(e)));
        setEdits((n) => n + 1);
        onChanged();
        onSelect({ from: e.from, to: e.to, type: e.type });
      } catch (err) {
        setError(poseGraphErrorCopy(err, 'add'));
      } finally {
        setBusy(false);
      }
    },
    [onChanged, onSelect],
  );

  return (
    <div className={styles.inspector}>
      {edge ? (
        <EdgeDetails
          key={selKey}
          edge={edge}
          canEdit={canEdit}
          busy={busy}
          armed={confirm.armed === selKey}
          onArm={(el) => selKey && confirm.arm(selKey, el)}
          onDisarm={confirm.disarm}
          onReject={() => void reject()}
          onCompare={() => onCompare(edge.from, edge.to)}
          onDeselect={() => onSelect(null)}
        />
      ) : (
        <div className={styles.empty}>
          <h2 className={styles.title}>Ingen kant valgt</h2>
          <p className={styles.muted}>
            Klik på en kant i modellen, eller vælg en af de mest belastede i listen. En høj residual betyder, at kanten
            trækker i modellen — typisk en forkert loop-lukning.
          </p>
        </div>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {canEdit && (
        <section className={styles.section} aria-label="Tilføj kant">
          {addOpen ? (
            <AddEdgeForm
              draft={draft}
              onDraft={setDraft}
              nodes={nodes}
              onAdded={(ref) => {
                setEdits((n) => n + 1);
                setAddOpen(false);
                onChanged();
                onSelect(ref);
              }}
              onCancel={() => setAddOpen(false)}
            />
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDraft(edge ? emptyDraft(edge.from, edge.to) : emptyDraft());
                setAddOpen(true);
              }}
            >
              Tilføj kant…
            </Button>
          )}
        </section>
      )}

      {rejected.length > 0 && (
        <section className={styles.section} aria-labelledby="pg-rejected">
          <h3 id="pg-rejected" className={styles.heading}>
            Afvist i denne session
          </h3>
          <ul className={styles.rejectedList}>
            {rejected.map((e) => (
              <li key={edgeParam(e)} className={styles.rejectedRow}>
                <span className={styles.swatch} style={{ background: edgeSwatchCss(e.type) }} aria-hidden="true" />
                <span className="mono">
                  {e.from} – {e.to}
                </span>
                <span className={styles.muted}>{EDGE_TYPE_LABEL[e.type]}</span>
                {canEdit && (
                  <TextLink size="sm" disabled={busy} onClick={() => void restore(e)}>
                    Genopret
                  </TextLink>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {canEdit && <OptimizeSection edits={edits} onDone={() => setEdits(0)} />}
    </div>
  );
}

// -------------------------------------------------------------------------- //

function EdgeDetails({
  edge,
  canEdit,
  busy,
  armed,
  onArm,
  onDisarm,
  onReject,
  onCompare,
  onDeselect,
}: {
  edge: PoseGraphEdge;
  canEdit: boolean;
  busy: boolean;
  armed: boolean;
  onArm: (el: HTMLElement) => void;
  onDisarm: () => void;
  onReject: () => void;
  onCompare: () => void;
  onDeselect: () => void;
}) {
  return (
    <section className={styles.details} aria-label="Valgt kant">
      <div className={styles.head}>
        <span className={styles.swatch} style={{ background: edgeSwatchCss(edge.type) }} aria-hidden="true" />
        <h2 className={styles.title}>{EDGE_TYPE_LABEL[edge.type]}</h2>
        <IconButton variant="ghost" size="sm" className={styles.close} label="Ryd valget" onClick={onDeselect}>
          <XIcon />
        </IconButton>
      </div>

      <div className={styles.thumbs}>
        <FrameThumb id={edge.from} role="Fra" />
        <span className={styles.arrow} aria-hidden="true">
          ↔
        </span>
        <FrameThumb id={edge.to} role="Til" />
      </div>

      <dl className={styles.facts}>
        <div>
          <dt>Residual</dt>
          <dd className="mono">{formatResidual(edge.residual)}</dd>
        </div>
        <div>
          <dt>Vægt (1/σ²)</dt>
          <dd className="mono">{edge.weight === undefined || edge.weight === null ? '–' : formatResidual(edge.weight)}</dd>
        </div>
      </dl>

      <div className={styles.actions}>
        <Button variant="outline" size="sm" onClick={onCompare}>
          Sammenlign billederne
        </Button>
        {canEdit && (
          <Button
            variant="outline"
            tone="danger"
            size="sm"
            armed={armed}
            disabled={busy}
            onBlur={onDisarm}
            onClick={(e) => (armed ? onReject() : onArm(e.currentTarget))}
          >
            {busy ? 'Afviser…' : armed ? 'Bekræft: afvis kanten' : 'Afvis kant'}
          </Button>
        )}
      </div>
      {canEdit && (
        <p className={styles.muted}>
          En afvist kant indgår ikke i optimeringen og tilføjes ikke igen af kantsøgningen. Den kan genoprettes.
        </p>
      )}

      <IcpCheck from={edge.from} to={edge.to} />
    </section>
  );
}

// -------------------------------------------------------------------------- //

function FrameThumb({ id, role }: { id: number; role: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure className={styles.thumb}>
      {failed ? (
        <span className={styles.thumbMissing}>Intet billede</span>
      ) : (
        <img
          className={styles.thumbImg}
          src={api.frameImageUrl(id, 'color', { maxSize: 160 })}
          alt={`Billede ${id}`}
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
      <figcaption className={styles.thumbCaption}>
        {role} <span className="mono">{id}</span>
      </figcaption>
    </figure>
  );
}

// -------------------------------------------------------------------------- //

/** Kør ICP on a pair: how well the two depth clouds line up from the stored poses. */
function IcpCheck({ from, to, onResult }: { from: number | null; to: number | null; onResult?: (r: IcpRefineResult) => void }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<IcpRefineResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResult(null);
    setError(null);
  }, [from, to]);

  const run = async () => {
    if (from === null || to === null || from === to) {
      setError('Vælg to forskellige billeder først.');
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const r = await api.refinePoseGraphIcp({ from, to });
      setResult(r);
      onResult?.(r);
    } catch (err) {
      setError(poseGraphErrorCopy(err, 'icp'));
    } finally {
      setRunning(false);
    }
  };

  const verdict = result ? icpVerdict(result) : null;
  return (
    <div className={styles.icp}>
      <div className={styles.icpHead}>
        <Button variant="outline" size="sm" onClick={() => void run()} disabled={running}>
          {running ? 'Kører ICP…' : 'Kør ICP'}
        </Button>
        <span className={styles.muted}>Lægger de to dybdeskyer oven i hinanden fra de gemte positioner.</span>
      </div>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      {result && verdict && (
        <>
          <dl className={styles.facts}>
            <div>
              <dt>RMS-afvigelse</dt>
              <dd className="mono">{(result.fitness * 100).toLocaleString('da-DK', { maximumFractionDigits: 1 })} cm</dd>
            </div>
            <div>
              <dt>Overlap (5 cm)</dt>
              <dd className="mono">{Math.round(result.inlier_fraction * 100)} %</dd>
            </div>
            <div>
              <dt>Konvergeret</dt>
              <dd>{result.converged ? 'Ja' : 'Nej'}</dd>
            </div>
          </dl>
          <p className={`${styles.verdict} ${styles[`verdict_${verdict.tone}`]}`}>{verdict.text}</p>
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------- //

function AddEdgeForm({
  draft,
  onDraft,
  nodes,
  onAdded,
  onCancel,
}: {
  draft: EdgeDraft;
  onDraft: (d: EdgeDraft) => void;
  nodes: ReadonlySet<number> | null;
  onAdded: (ref: EdgeRef) => void;
  onCancel: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggested, setSuggested] = useState<number | null>(null);
  const set = (patch: Partial<EdgeDraft>) => {
    setError(null);
    onDraft({ ...draft, ...patch });
  };

  const fromId = /^\d+$/.test(draft.from.trim()) ? Number(draft.from) : null;
  const toId = /^\d+$/.test(draft.to.trim()) ? Number(draft.to) : null;

  const submit = async () => {
    const check = validateEdgeDraft(draft, nodes);
    if (!check.ok) {
      setError(check.error);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const saved = await api.addPoseGraphEdge(check.edge);
      onAdded({ from: saved.from, to: saved.to, type: saved.type });
    } catch (err) {
      setError(poseGraphErrorCopy(err, 'add'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <h3 className={styles.heading}>Tilføj kant</h3>
      <div className={styles.formGrid}>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Fra billede</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            mono
            inputMode="numeric"
            value={draft.from}
            onChange={(e) => {
              setSuggested(null);
              set({ from: e.target.value });
            }}
            placeholder="fx 101"
          />
        </label>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Til billede</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            mono
            inputMode="numeric"
            value={draft.to}
            onChange={(e) => {
              setSuggested(null);
              set({ to: e.target.value });
            }}
            placeholder="fx 112"
          />
        </label>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Type</span>
          <Select
            variant="outline"
            size="sm"
            value={draft.type}
            onChange={(e) => set({ type: e.target.value as PoseGraphEdgeType })}
          >
            {EDGE_TYPES.map((t) => (
              <option key={t} value={t}>
                {EDGE_TYPE_LABEL[t]}
              </option>
            ))}
          </Select>
        </label>
        <label className={controls.field}>
          <span className={controls.fieldLabel}>Vægt</span>
          <TextInput
            variant="outline"
            size="sm"
            block
            mono
            inputMode="decimal"
            value={draft.weight}
            onChange={(e) => set({ weight: e.target.value })}
          />
        </label>
      </div>

      <IcpCheck from={fromId} to={toId} onResult={(r) => setSuggested(suggestWeight(r))} />
      {suggested !== null && (
        <p className={styles.suggest}>
          Foreslået vægt ud fra ICP: <span className="mono">{formatWeight(suggested)}</span>
          <TextLink size="sm" onClick={() => set({ weight: formatWeight(suggested) })}>
            Brug den
          </TextLink>
        </p>
      )}

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" size="sm" disabled={submitting}>
          {submitting ? 'Tilføjer…' : 'Tilføj kant'}
        </Button>
        <Button variant="outline" size="sm" onClick={onCancel}>
          Annullér
        </Button>
      </div>
      <p className={styles.muted}>Residualen beregnes ved næste optimering.</p>
    </form>
  );
}

// -------------------------------------------------------------------------- //

function OptimizeSection({ edits, onDone }: { edits: number; onDone: () => void }) {
  const { jobs } = useJobs();
  const job = latestOptimizeJob(jobs);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const running = job?.status === 'queued' || job?.status === 'running';

  const run = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api.submitJob({ stage: 'optimize' });
      onDone();
    } catch (err) {
      setError(poseGraphErrorCopy(err, 'optimize'));
    } finally {
      setSubmitting(false);
    }
  };

  const status = optimizeStatusText(job);
  return (
    <section className={`${styles.section} ${styles.optimize}`} aria-labelledby="pg-optimize">
      <h3 id="pg-optimize" className={styles.heading}>
        Optimering
      </h3>
      <p className={styles.muted}>
        {edits > 0
          ? `${edits} ${edits === 1 ? 'ændring' : 'ændringer'} i kanterne venter — positionerne flyttes først, når optimeringen er kørt.`
          : 'Ændringer i kanterne flytter først positionerne, når optimeringen er kørt igen.'}
      </p>
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <Button
        variant={edits > 0 ? 'primary' : 'outline'}
        size="sm"
        onClick={() => void run()}
        disabled={submitting || running}
      >
        {running ? 'Optimerer…' : submitting ? 'Starter…' : 'Kør optimering'}
      </Button>
      {status && (
        <p className={styles.muted} role="status">
          {status}
        </p>
      )}
    </section>
  );
}
